// Topics store
// --------------------------------------------------------
import Vue from 'vue'
import axios from 'axios'
import FileSaver from 'file-saver'
import api from '@/lib/api'
import EventHub from '@/lib/eventHub'
import {GA_EVENTS} from '@/lib/ga'
import {Events, AlertMessages} from '@/lib/definitions'
import utils from './landscape/utils'

let topicsLoadedAt = 0
const state = {
  topics: null,
  topic: null, // current topic
  currentTopics: null, // only used to get prev/next topics
  comment: null, // only used for edit,
  bulkTopics: [], // used for bulk editing
  newTopics: false
}
const getters = {
  newTopics: state => state.newTopics,
  currentTopic: state => state.topic,
  prevTopic: state => {
    let result = null
    if (state.currentTopics && state.topic) {
      const index = state.currentTopics.findIndex(
        topic => topic._id === state.topic._id
      )
      if (index > 0) {
        return state.currentTopics[index - 1]._id
      }
    }
    return result
  },
  nextTopic: state => {
    let result = null
    if (state.currentTopics && state.topic) {
      const index = state.currentTopics.findIndex(
        topic => topic._id === state.topic._id
      )
      if (index !== state.currentTopics.length - 1) {
        return state.currentTopics[index + 1]._id
      }
    }
    return result
  },
  currentComment: state => state.comment,
  allTopics: state => {
    return state.topics
  },
  allTopicsLength: state => {
    return state.topics?.length || 0
  },
  topics: (state, getters) => {
    const actor = getters.selectedActor
    return !state.topics
      ? []
      : !actor
      ? state.topics
      : state.topics.filter(topic => {
          return (
            topic.parentsId.includes(actor._id) ||
            topic.relatedActors.find(actorId => {
              const relatedActor = getters.getActorByID(actorId)
              if (relatedActor.ancestors) {
                const ancestors = relatedActor.ancestors
                return ancestors.includes(actor._id)
              } else {
                return false
              }
            })
          )
        })
  },
  getTopicByTopicId: state => topicId => {
    if (state.topics) {
      return state.topics.find(topic => topic._id === topicId)
    }
  },
  bulkTopics: state => state.bulkTopics
}
// Helpers
function getEndpointToTopic(context, topic) {
  topic = topic || context.getters.currentTopic
  const landscapeId = context.getters.currentLandscape._id
  const actorId = topic.actorId
  const topicId = topic._id
  return `landscapes/${landscapeId}/actors/${actorId}/topics/${topicId}`
}
function getEndpointToComment(context, topic = context.getters.currentTopic) {
  return getEndpointToTopic(context, topic) + '/comments'
}

const mutations = {
  setNewTopics(state, value) {
    state.newTopics = value
  },
  setTopics(state, topics) {
    topicsLoadedAt = Date.now()
    if (state.topics) state.topics = null
    state.topics = topics
  },
  removeTopics(state) {
    if (state.topics) state.topics = null
  },
  addTopic(state, topic) {
    if (!state.topics) state.topics = []
    state.topics.push(topic)
  },
  deleteTopic(state, topicId) {
    let index = state.topics.findIndex(t => t._id === topicId)
    state.topics.splice(index, 1)
  },
  setCurrentTopic(state, topic) {
    state.topic = topic
    if (state.topics) {
      let index = state.topics.findIndex(t => t._id === topic._id)
      state.topics.splice(index, 1, topic)
    } // else === loading topic outside TopicsView
  },
  preloadCurrentTopic(state, topicId) {
    if (state.topics) {
      state.topic = state.topics.find(t => t._id === topicId)
    }
  },
  removeCurrentTopic(state) {
    state.topic = null
  },
  setCurrentTopics(state, topics) {
    state.currentTopics = topics
  },
  removeCurrentTopics(state) {
    state.currentTopics = null
  },
  voteTopic(state, {topic, vote}) {
    if (vote.positiveVote) {
      topic.positiveVotes++
    } else if (
      topic.currentUser.positiveVote &&
      (vote.positiveVote === false || vote.negativeVote)
    ) {
      topic.positiveVotes--
    }
    topic.currentUser.positiveVote = vote.positiveVote || false
    topic.currentUser.negativeVote = vote.negativeVote || false
    // TODO check negativeVotes for flag
  },
  favoriteTopic(state, {topic, value}) {
    topic.currentUser.favorited = value // !?
  },
  markAsReadTopic(state, topic) {
    topic.currentUser.visited = true
  },
  markAllAsRead(state) {
    state.topics.map(topic => (topic.currentUser.visited = true))
  },
  markCommentsAsRead(state) {
    state.topic.currentUser.unreadComments = 0
  },
  // COMMENT
  addComment(state, comment) {
    state.topic.comments.push(comment)
    state.topic.commentCount++
    state.topic.currentUser.unreadComments = 0 // Hack to prevent mark as 'new' your own topic
  },
  deleteComment(state, commentId) {
    const index = state.topic.comments.findIndex(c => c._id === commentId)
    state.topic.comments.splice(index, 1)
    state.topic.commentCount--
  },
  updateComment(state, comment) {
    const index = state.topic.comments.findIndex(c => c._id === comment._id)
    state.topic.comments.splice(index, 1, comment)
  },
  setCurrentComment(state, commentId) {
    state.comment = state.topic.comments.find(
      comment => comment._id === commentId
    )
  },
  removeCurrentComment(state) {
    state.comment = null
  },
  voteComment(state, payload) {
    const type = payload.vote
    const comment = state.topic.comments.find(c => c._id === payload.commentId)
    if (type.positiveVote) {
      comment.positiveVotes++
    } else if (
      comment.currentUser.positiveVote &&
      (type.positiveVote === false || type.negativeVote)
    ) {
      comment.positiveVotes--
    }
    comment.currentUser.positiveVote = type.positiveVote || false
    comment.currentUser.negativeVote = type.negativeVote || false
    // TODO Refactor voteTopic + voteComment
  },
  // BULK EDITING
  addBulkTopic(state, topicId) {
    const index = state.bulkTopics.findIndex(id => id === topicId)
    if (index === -1) state.bulkTopics.push(topicId)
    else state.bulkTopics.splice(index, 1)
  },
  addBulkTopics(state, arrTopicsId) {
    state.bulkTopics = arrTopicsId
  },
  resetBulkTopics(state) {
    state.bulkTopics = []
  }
}

const actions = {
  updateNewTopics: (context, res) => {
    context.commit('setNewTopics', new Date(res.topics) > topicsLoadedAt)
  },
  unloadTopics: context => context.commit('removeTopics'),
  unloadTopic: context => context.commit('removeCurrentTopic'),
  markCommentsAsRead: context => context.commit('markCommentsAsRead'),
  exportTopics: (context, payload) => {
    const landscapeId = context.getters.currentLandscape._id
    // payload: Obj w properties:
    // actor: String actorId
    // topics: Array of topic id strings
    // minDate, maxDate
    return axios
      .post(
        `${process.env.VUE_APP_API_URL}/landscapes/${landscapeId}/topics/export`,
        payload,
        {responseType: 'blob'}
      )
      .then(data => {
        const contentDispHeader = data.headers['content-disposition']
        const filename = contentDispHeader
          .split('filename=')[1]
          .split(';')[0]
          .slice(1, -1)

        FileSaver.saveAs(data.data, filename)
      })
  },
  loadTopics: context => {
    const landscapeId = context.getters.currentLandscape._id
    return api
      .call('get', `landscapes/${landscapeId}/topics`)
      .then(response => {
        if (response.topics.length > 0) {
          utils.parseTopics(response.topics, context)
          context.commit('setNewTopics', false)
          context.commit('setTopics', response.topics)
          if (context.state.topic) {
            // If currentTopic exists it has more information
            // than the one of the list. Sync.
            context.commit('setCurrentTopic', context.state.topic)
          }
        }
        return true
      })
  },
  loadTopicsByKeywords: (context, payload) => {
    const landscapeId = context.getters.currentLandscapeId
    payload.actorId = context.getters.selectedActorID
    return api
      .call('post', `landscapes/${landscapeId}/analysis/topics`, payload)
      .then(response => {
        utils.parseTopics(response.topics, context)
        return response.topics
      })
  },
  setCurrentTopics: ({commit}, topics) => {
    commit('setCurrentTopics', topics)
  },
  removeCurrentTopics: ({commit}) => {
    commit('removeCurrentTopics')
  },
  preloadTopic: (context, topicId) =>
    context.commit('preloadCurrentTopic', topicId),
  loadTopic: (context, topicId) => {
    const landscapeId = context.getters.currentLandscapeId
    if (!landscapeId) return Promise.reject()
    // this.$store.dispatch('preloadTopic', tq.topic)
    context.commit('preloadCurrentTopic', topicId) // Improves UX
    return api
      .call('get', `landscapes/${landscapeId}/topics/${topicId}`)
      .then(topic => {
        utils.parseTopics([topic], context)
        context.commit('setCurrentTopic', topic)
      })
  },
  markAsReadTopic: (context, topic) => {
    context.commit('markAsReadTopic', topic) // Optimistic
    const landscapeId = context.getters.currentLandscape._id
    const topicId = topic._id
    return api.call('post', `landscapes/${landscapeId}/topics/${topicId}/read`)
  },
  toJira: (context, payload) => {
    const landscapeId = context.getters.currentLandscape._id
    const topicId = payload.topicId
    const jiraProject = payload.jiraProject
    const issueType = payload.issueType
    return api
      .call(
        'post',
        `superadmin/landscapes/${landscapeId}/topics/${topicId}/jira/${jiraProject}`,
        {issueType}
      )
      .then(res => res.url)
  },
  topicFromUrl: (context, url) => {
    const landscapeId = context.getters.currentLandscape._id
    return api.call('post', `landscapes/${landscapeId}/topics/url`, {url})
  },
  createTopic: (context, payload) => {
    Vue.$ga.event(GA_EVENTS.TOPIC_CREATE)
    const landscapeId = context.getters.currentLandscape._id
    const actorId = payload.actorId || context.getters.selectedActor._id
    return api
      .call(
        'post',
        `landscapes/${landscapeId}/actors/${actorId}/topics`,
        payload
      )
      .then(topic => {
        utils.parseTopics([topic], context)
        context.commit('addTopic', topic)
        context.commit('updateLandscapeTags', topic.tags)
        if (topic.currentUser.feedback) {
          // show first time message
          EventHub.emit(
            Events.SHOW_SNACKBAR,
            AlertMessages.FEEDBACK_NEW_TOPIC.replace(
              '##landscape##',
              context.getters.currentLandscape.name
            ),
            null,
            null,
            null,
            9000
          )
        }
        return topic._id
      })
  },
  editTopic: (context, payload) => {
    return api
      .call('patch', getEndpointToTopic(context), payload)
      .then(topic => {
        context.commit('updateLandscapeTags', topic.tags)
        utils.parseTopics([topic], context)
        context.commit('setCurrentTopic', topic)
      })
  },
  searchTopics: (context, payload) => {
    const landscapeId = context.getters.currentLandscape._id
    return api.call('post', `landscapes/${landscapeId}/topics/search`, payload)
  },
  deleteTopic: (context, topic) => {
    return api
      .call('delete', getEndpointToTopic(context, topic))
      .then(() => context.commit('deleteTopic', topic._id))
  },
  voteTopic: (context, payload) => {
    context.commit('voteTopic', payload) // Optimistic
    return api.call(
      'post',
      `${getEndpointToTopic(context, payload.topic)}/vote`,
      payload.vote
    )
  },
  favoriteTopic: (context, payload) => {
    // Optimistic
    context.commit('favoriteTopic', payload)
    return api.call(
      'post',
      `${getEndpointToTopic(context, payload.topic)}/favorite`,
      {favorite: payload.value}
    )
  },
  // COMMENTS
  setComment: (context, commentId) =>
    context.commit('setCurrentComment', commentId),
  unsetComment: context => context.commit('removeCurrentComment'),
  createComment: (context, payload) => {
    return api
      .call('post', getEndpointToComment(context), payload)
      .then(comment => context.commit('addComment', comment))
  },
  editComment: (context, payload) => {
    const commentId = context.getters.currentComment._id
    return api
      .call('put', `${getEndpointToComment(context)}/${commentId}`, payload)
      .then(comment => context.commit('updateComment', comment))
  },
  deleteComment: (context, commentId) => {
    return api
      .call('delete', `${getEndpointToComment(context)}/${commentId}`)
      .then(() => context.commit('deleteComment', commentId))
  },
  voteComment: (context, payload) => {
    context.commit('voteComment', payload) // Optimistic
    const commentId = payload.commentId
    return api.call(
      'post',
      `${getEndpointToComment(context)}/${commentId}/vote/`,
      payload.vote
    )
  },
  // BULK EDITING
  bulkTopics: ({getters, commit, dispatch}, payload) => {
    const landscapeId = getters.currentLandscape._id
    const ids = getters.bulkTopics
    const topics = ids.map(id => getters.getTopicByTopicId(id))
    const action = payload.action
    let verb = 'post'
    let path = '/'
    let body = {ids}
    switch (action) {
      case 'export':
        dispatch('exportTopics', {topics: ids})
        return
      case 'delete':
        verb = 'delete'
        body = {data: {ids}}
        break
      case 'move':
        path = '/move'
        body.actorId = payload.actorId
        break
      case 'read':
        path = '/read'
        break
      default:
        return Promise.reject(new Error('BulkTopics: not valid action'))
    }
    return api
      .call(verb, `landscapes/${landscapeId}/topics${path}`, body)
      .then(() => {
        commit('resetBulkTopics')
        if (action === 'move') dispatch('loadTopics')
        else if (action === 'read')
          topics.forEach(topic => commit('markAsReadTopic', topic))
        else if (action === 'delete')
          topics.forEach(topic => commit('deleteTopic', topic._id))
      })
  },
  markAllAsRead: ({getters, commit}) => {
    commit('markAllAsRead') // Optimistic
    const landscapeId = getters.currentLandscape._id
    return api.call('post', `landscapes/${landscapeId}/topics/read`)
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
